import React from 'react';
import proptypes from 'prop-types';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { ThemeProvider } from '@one-thd/sui-atomic-components';
import { getThemeByName } from '@thd-olt-component-react/stencil-themes';
import {
  arrayOf as arrayOfType,
  extend,
  params,
  shape as shapeType,
  typename,
  string as stringType,
  bool as boolType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { bentoHeroDataModel } from './dataModel/bentoHero';
import { BentoHeroContainer } from './BentoHeroContainer';
import { normalizeBentoHeroData } from './normalizeBentoHeroData';

const {
  arrayOf, bool, number, shape, string
} = proptypes;

const BentoHero = ({
  componentId = '',
  componentClass = '',
  componentPosition = 1,
  enableB2CRadius = false,
  livePreviewData = null,
  mediaCollectionIndex = 0,
  renderDefault = false,
  slotContentId: slotName = '',
  variationIds = []
}) => {
  const { data } = useDataModel('component', {
    variables: {
      id: componentId,
      componentClass,
    },
    ssr: true,
    fetchPolicy: 'cache-first',
    skip: !!slotName
  });

  const componentData = livePreviewData ? { ...livePreviewData, isLivePreviewData: true } : data;

  const {
    data: slotData
  } = useDataModel('slot', {
    fetchPolicy: 'cache-only',
    variables: {
      id: slotName,
    },
    skip: !slotName,
  });

  // Normalize data from different sources
  const normalizedData = normalizeBentoHeroData({
    propsData: livePreviewData,
    slotData,
    componentData,
    mediaCollectionIndex
  });

  if (!normalizedData || !normalizedData.data) {
    return null;
  }

  const { data: bentoheroData } = normalizedData;

  return (
    <ImpressionProvider
      key={componentPosition}
      data={{
        id: componentId || slotName,
        component: BentoHero.displayName,
        name: BentoHero.displayName,
        type: 'content'
      }}
    >
      <ThemeProvider theme={getThemeByName(bentoheroData?.theme)}>
        <BentoHeroContainer
          data={bentoheroData}
          enableB2CRadius={enableB2CRadius}
          renderDefault={renderDefault}
          variationIds={variationIds}
        />
      </ThemeProvider>
    </ImpressionProvider>
  );
};

BentoHero.propTypes = {
  componentId: string,
  componentClass: string,
  componentPosition: number,
  enableB2CRadius: bool,
  livePreviewData: shape({}),
  mediaCollectionIndex: number,
  renderDefault: bool,
  slotContentId: string,
  variationIds: arrayOf(shape({}))
};

BentoHero.displayName = 'BentoHero';

BentoHero.dataModel = extend(
  {
    slot: params({
      id: stringType().isRequired(),
    }).shape({
      id: stringType(),
      entryId: stringType(),
      mediaCollections: arrayOfType(shapeType({
        title: stringType(),
        entryId: stringType(),
        theme: stringType(),
        media: arrayOfType(shapeType({
          entryId: stringType(),
          title: stringType(),
          subtitle: stringType(),
          description: stringType(),
          seo: boolType(),
          isSponsored: boolType(),
          isCarousel: boolType(),
          schema: stringType(),
          customProps: stringType(),
          sourceDesktop: arrayOfType(shapeType({
            entryId: stringType(),
            title: stringType(),
            type: stringType(),
            uri: stringType(),
            altText: stringType(),
            hyperlink: stringType(),
            hotspot: stringType()
          })),
          sourceMobile: arrayOfType(shapeType({
            entryId: stringType(),
            title: stringType(),
            type: stringType(),
            uri: stringType(),
            hyperlink: stringType(),
            altText: stringType(),
            hotspot: stringType()
          })),
        }))
      })),
      __typename: typename('newSlot'),
    }),
  },
  bentoHeroDataModel);

export { BentoHero };